<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Prospects</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Prospects</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewProspect" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Prospect</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callHelp" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-question-circle mr-2"></i>
                                    <span>Help</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callSupport" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-info-circle mr-2"></i>
                                    <span>Contact Support</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="prospects"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['firstName', 'lastName', 'email', 'mobile', 'source']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search First Name, Last Name, Email, Mobile or Source"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No prospects found.
                        </template>

                        <template #loading>
                            Loading prospects. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <div v-if="((data.salesStage != 'W') && (data.salesStage != 'L'))">
                                    <SplitButton
                                        icon="pi pi-pencil"
                                        label="Select"
                                        :model="getFilteredItems(data)"
                                        severity="success"
                                        @click="chooseBlankSelection(data)"
                                        class="mb-2 mr-2 ml-2 mt-2"
                                    />
                                </div>
                            </template>
                        </Column>

                        <Column field="id" header="ID" :sortable="true"/>

                        <Column field="created" header="Created" style="max-width:8rem">
                            <template #body="{data}">
                                <span class="p-column-title">Created</span>
                                {{ convertDateIntoLocalTimezone(data.created) }}
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'salesStage'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Sales Stage</span>
                                    <span v-if="data.salesStage === 'I'"
                                          :class="'customer-badge status-' + data.salesStage">Initialised</span>
                                    <span v-if="data.salesStage === 'M'"
                                          :class="'customer-badge status-' + data.salesStage">Contacted</span>
                                    <span v-if="data.salesStage === 'Q'"
                                          :class="'customer-badge status-' + data.salesStage">Qualifying</span>
                                    <span v-if="data.salesStage === 'N'"
                                          :class="'customer-badge status-' + data.salesStage">Nurturing</span>
                                    <span v-if="data.salesStage === 'P'"
                                          :class="'customer-badge status-' + data.salesStage">Offer Presented</span>
                                    <span v-if="data.salesStage === 'O'"
                                          :class="'customer-badge status-' + data.salesStage">Objection Handling</span>
                                    <span v-if="data.salesStage === 'S'"
                                          :class="'customer-badge status-' + data.salesStage">Activated Subscription</span>
                                    <span v-if="data.salesStage === 'W'"
                                          :class="'customer-badge status-' + data.salesStage">Won</span>
                                    <span v-if="data.salesStage === 'L'"
                                          :class="'customer-badge status-' + data.salesStage">Lost</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'amount'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Amount</span>
                                    {{ formatAmount(data.amount) }}
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Prospect {{ this.prospect.firstName + " " + this.prospect.lastName }}</h3>

                    <div class="field">
                        <label for="firstName">First Name</label>
                        <InputText id="firstName" v-model.trim="prospect.firstName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="lastName">Last Name</label>
                        <InputText id="lastName" v-model.trim="prospect.lastName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="orgName">Organisation Name</label>
                        <InputText id="orgName" v-model.trim="prospect.orgName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="prospect.email" required="true"/>
                    </div>

                    <div class="field">
                        <label for="mobile">Mobile</label>
                        <br>
                        <InputText id="mobile" v-model.trim="prospect.mobile" required="true"/>
                    </div>

                    <div class="field">
                        <label for="source">Source</label>
                        <InputText id="source" v-model.trim="prospect.source" required="true"/>
                    </div>

                    <div class="field">
                        <label for="user">User</label>
                        <Dropdown v-model="prospect.user" :options="filteredUsers"
                                  optionLabel="username"
                                  optionValue="id"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="product">Product</label>
                        <Dropdown v-model="prospect.product" :options="companyProducts"
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="salesStage">Sales Stage</label>
                        <Dropdown v-model="prospect.salesStage" :options="salesStageTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <div class="field">
                        <label for="note">Note</label>
                        <Editor v-model="prospect.note" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="prospect.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Action"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :loading="submitted" @click="updateProspect"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Prospect {{ this.prospect.firstName + " " + this.prospect.lastName }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Prospect?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted" @click="deleteProspect"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Prospect</h3>

                    <div class="field">
                        <label for="firstName">First Name</label>
                        <InputText id="firstName" v-model.trim="newProspect.firstName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="lastName">Last Name</label>
                        <InputText id="lastName" v-model.trim="newProspect.lastName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="orgName">Organisation Name</label>
                        <InputText id="orgName" v-model.trim="newProspect.orgName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="newProspect.email" required="true"/>
                    </div>

                    <div class="field">
                        <label for="mobile">Mobile</label>
                        <InputText id="mobile" v-model.trim="newProspect.mobile" required="true"/>
                    </div>

                    <div class="field">
                        <label for="source">Source</label>
                        <InputText id="source" v-model.trim="newProspect.source" required="true"/>
                    </div>

                    <div class="field">
                        <label for="product">Product</label>
                        <Dropdown v-model="newProspect.product" :options="companyProducts"
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="note">Note</label>
                        <Editor v-model="newProspect.note" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :loading="submitted" @click="createProspect"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Complete Prospect {{ this.prospect.firstName + " " + this.prospect.lastName }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Mark as Won and Create Client?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Complete" class="mr-2 mb-2" :loading="submitted" @click="completeProspect"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Send Email for Prospect {{ this.prospect.firstName + " " + this.prospect.lastName }}</h3>

                    <div class="field">
                        <label for="toolboxEmailSubject">Email Subject</label>
                        <InputText id="toolboxEmailSubject"
                                   v-model.trim="toolbox.emailSubject"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="toolboxEmailMessage">Email Message</label>
                        <Editor v-model="toolbox.emailMessage" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                                    <span class="ql-formats">
                                                        <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                                        <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                                        <button class="ql-underline"
                                                                v-tooltip.bottom="'Underline'"></button>
                                                        <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                                        <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                                        <button class="ql-background"
                                                                v-tooltip.bottom="'Color'"></button>
                                                        <button class="ql-list" value="bullet"
                                                                v-tooltip.bottom="'Bulleted List'"></button>
                                                        <button class="ql-list" value="ordered"
                                                                v-tooltip.bottom="'Ordered List'"></button>
                                                        <button class="ql-indent" value="-1"
                                                                v-tooltip.bottom="'Indent -1'"></button>
                                                        <button class="ql-indent" value="+1"
                                                                v-tooltip.bottom="'Indent +1'"></button>
                                                    </span>
                            </template>
                        </Editor>
                    </div>

                    <Button label="Send Email" class="mr-2 mb-2" :loading="submitted" @click="sendEmail"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Send SMS for Prospect {{ this.prospect.firstName + " " + this.prospect.lastName }}</h3>

                    <div class="field">
                        <label for="toolboxSmsMessage">SMS Message</label>
                        <InputText id="toolboxSmsMessage"
                                   v-model.trim="toolbox.smsMessage"
                                   required="true"/>
                    </div>

                    <Button label="Send SMS" class="mr-2 mb-2" :loading="submitted" @click="sendSms"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Add Audit Trail for Prospect {{ this.prospect.firstName + " " + this.prospect.lastName }}</h3>

                    <div class="field">
                        <label for="auditTrailSubProcess">Audit Trail Sub Process</label>
                        <Dropdown v-model="toolbox.auditTrailSubProcess" :options="subProcessTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="auditTrailDescription">Audit Trail Description</label>
                        <Textarea v-model="toolbox.auditTrailDescription" :autoResize="true"
                                  rows="10" cols="50"/>
                    </div>

                    <Button label="Add Audit Trail" class="mr-2 mb-2" :loading="submitted"
                            @click="addAuditTrail"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import ProspectService from "@/service/ProspectService";
import ProductService from "@/service/ProductService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'status', header: 'Status', sortable: true},
            {field: 'salesStage', header: 'Sales Stage', sortable: true},
            {field: 'productName', header: 'Product Name', sortable: true},
            {field: 'firstName', header: 'First Name', sortable: true},
            {field: 'lastName', header: 'Last Name', sortable: true},
            {field: 'email', header: 'Email', sortable: true},
            {field: 'mobile', header: 'Mobile', sortable: true},
            {field: 'source', header: 'Source', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'status', header: 'Status', sortable: true},
            {field: 'salesStage', header: 'Sales Stage', sortable: true},
            {field: 'productName', header: 'Product Name', sortable: true},
            {field: 'firstName', header: 'First Name', sortable: true},
            {field: 'lastName', header: 'Last Name', sortable: true},
            {field: 'email', header: 'Email', sortable: true},
            {field: 'mobile', header: 'Mobile', sortable: true},
            {field: 'source', header: 'Source', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'new', header: 'New'},
            {field: 'won', header: 'Won'},
            {field: 'lost', header: 'Lost'}
        ];

    },
    async mounted() {
        try {
            const userPingResponse = await UserService.getUserPing();
            console.log("[PmOverviewDashboard][mounted] System is active.");
            console.log("[PmOverviewDashboard][mounted] Data = ", userPingResponse);
        } catch (e) {
            console.log("[PmOverviewDashboard][mounted] System is NOT active.");
            console.log("[PmOverviewDashboard][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            console.log("[PmOverviewDashboard][mounted] User is NOT defined.");
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            prospect: {},
            newProspect: {},
            prospects: null,
            originalProspects: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            filteredUsers: [],
            companyProducts: [],
            toolbox: {},
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            salesStageTypes: [
                {name: 'Initialised', code: 'I'},
                {name: 'Contacted', code: 'M'},
                {name: 'Qualifying', code: 'Q'},
                {name: 'Nurturing', code: 'N'},
                {name: 'Offer Presented', code: 'P'},
                {name: 'Objection Handling', code: 'O'},
                {name: 'Activated Subscription', code: 'S'},
                {name: 'Won', code: 'W'},
                {name: 'Lost', code: 'L'}
            ],
            subProcessTypes: [
                {name: 'Prospect Discovery', code: 'Prospect Discovery'},
                {name: 'Prospect Outreach', code: 'Prospect Outreach'},
                {name: 'Prospect Engagement', code: 'Prospect Engagement'}
            ]
        }
    },
    watch: {
        suppliers() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                try {
                    // Attempt to get user data
                    const userData = await UserService.getUser(this.userStore.user.id);
                    this.profile = userData.data;
                } catch (e) {
                    console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                    this.userStore.clearUser();
                    this.$router.push('login');
                    return;
                }

                this.profile.companyId = this.userStore.activeCompanyId;

                const [userConfigurationData, companyConfigurationData, prospectsData, usersData, productsData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    ProspectService.getCompanyProspects(this.profile.companyId),
                    UserService.getCompanyUsers(this.profile.companyId),
                    ProductService.getCompanyProducts(this.profile.companyId)
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                this.prospects = prospectsData.data;
                this.originalProspects = [...this.prospects];

                this.filteredUsers = usersData.data;

                this.companyProducts = productsData.data;

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            if (this.$route.query.new === "1") {
                this.callNewAuditTrail();
            }

            console.log("[PhoneCalls][mounted] this.prospects = ", this.prospects);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);
            console.log("[PhoneCalls][mounted] this.filteredUsers = ", this.filteredUsers);
            console.log("[PhoneCalls][mounted] this.companyProducts = ", this.companyProducts);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'firstName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'lastName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'email': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'mobile': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'source': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewProspect() {
            this.newProspect = {};
            this.activeTab = 4;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callUpdateProspect(data) {
            this.activeTab = 2;
            this.prospect = data;
        },
        callDeleteProspect(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.prospect = data;
        },
        callCompleteProspect(data) {
            this.activeTab = 5;
            this.prospect = data;
        },
        callSendEmail(data) {
            this.activeTab = 6;
            this.prospect = data;
        },
        callSendSms(data) {
            this.activeTab = 7;
            this.prospect = data;
        },
        callAddAuditTrail(data) {
            this.activeTab = 8;
            this.prospect = data;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isNew = value.some(item => item.field === "new");
            const isWon = value.some(item => item.field === "won");
            const isLost = value.some(item => item.field === "lost");

            if (this.prospects !== null) {

                if (isNew) {
                    this.prospects = this.originalProspects.filter(prospect => prospect.salesStage === 'I');
                } else if (isWon) {
                    this.prospects = this.originalProspects.filter(prospect => prospect.salesStage === 'W');
                } else if (isLost) {
                    this.prospects = this.originalProspects.filter(prospect => prospect.salesStage === 'L');
                } else {
                    this.prospects = this.originalProspects;
                }

            } else {
                this.prospects = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            if ((data.salesStage != 'W') && (data.salesStage != 'L')) {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdateProspect(data);
                    }
                });

                items.push({
                    label: 'Mark as Complete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callCompleteProspect(data);
                    }
                });

                items.push({
                    label: 'Send Email',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callSendEmail(data);
                    }
                });

                items.push({
                    label: 'Send SMS',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callSendSms(data);
                    }
                });

                items.push({
                    label: 'Add Audit Trail',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callAddAuditTrail(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteProspect(data);
                    }
                });

            }

            console.log('[PhoneCalls][getFilteredItems] items = ', items);

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[PhoneCalls][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        convertDateIntoLocalTimezone(inputDate) {
            console.log("[convertDateIntoLocalTimezone] inputDate :", inputDate);

            try {
                const moment = require("moment-timezone");
                const inputTimeZone = "Etc/UTC"; // Assuming the input date is in UTC

                let targetTimeZone = "Europe/London";

                if (this.companyConfiguration.region == "ZA") {
                    targetTimeZone = "Africa/Johannesburg";
                }

                console.log("[convertDateIntoLocalTimezone] targetTimeZone :", targetTimeZone);

                // Parse the input date string and set the input time zone
                const parsedDate = moment.tz(inputDate, "YYYY-MM-DD HH:mm:ss", inputTimeZone);

                console.log("[convertDateIntoLocalTimezone] parsedDate :", parsedDate);

                // Convert the date to the target time zone
                const outputDate = parsedDate.clone().tz(targetTimeZone);

                console.log("[convertDateIntoLocalTimezone] Output Date :", outputDate.format("YYYY-MM-DD HH:mm:ss"));

                return outputDate.format("YYYY-MM-DD HH:mm:ss");

            } catch (error) {
                console.error("[convertDateIntoLocalTimezone] Could not convert date. Returning input date. Error :", error);

                return inputDate;
            }

        },
        async updateProspect() {
            console.log("[PhoneCalls][updateProspect] Start.");

            this.submitted = true;

            if (this.prospect.firstName) {

                try {
                    console.log('[PhoneCalls][updatePhoneCall] this.prospect = ', this.prospect);

                    var newProspectDto = {
                        productId: this.prospect.product,
                        userId: this.prospect.user,
                        companyId: this.profile.companyId,
                        firstName: this.prospect.firstName,
                        lastName: this.prospect.lastName,
                        orgName: this.prospect.orgName,
                        email: this.prospect.email,
                        mobile: this.prospect.mobile,
                        source: this.prospect.source,
                        note: this.prospect.note,
                        salesStage: this.prospect.salesStage,
                        status: this.prospect.status,
                    };

                    console.log("[" + name + "][editProspect] Before creating prospect back-end call. newProspectDto = ", newProspectDto);

                    const response = await ProspectService.updateProspect(this.prospect.id, newProspectDto);
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    const [prospectData] = await Promise.all([
                        ProspectService.getCompanyProspects(this.profile.companyId)
                    ]);

                    this.prospects = prospectData.data;
                    this.originalProspects = prospectData.data;
                    this.userStore.setProspects(prospectData.data);

                    console.log('[PhoneCalls][updatePhoneCall] this.prospects = ', this.prospects);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated prospect',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update prospect. Please try again.',
                        life: 3000
                    });
                }
            }

            this.prospect = {};
            this.activeTab = 1;
            this.submitted = false;

        },
        async deleteProspect() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.prospect.id) {

                    try {
                        // Delete the appointment
                        const response = await ProspectService.deleteProspect(this.prospect.id);
                        console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                        const [prospectData] = await Promise.all([
                            ProspectService.getCompanyProspects(this.profile.companyId)
                        ]);

                        this.prospects = prospectData.data;
                        this.originalProspects = prospectData.data;
                        this.userStore.setProspects(prospectData.data);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted prospect',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete prospect. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createProspect() {
            console.log("[PhoneCalls][updateProspect] Start.");

            this.submitted = true;

            if (this.newProspect.firstName) {

                try {

                    var newProspectDto = {
                        productId: this.newProspect.product,
                        userId: this.userStore.user.id,
                        firstName: this.newProspect.firstName,
                        lastName: this.newProspect.lastName,
                        orgName: this.newProspect.orgName,
                        email: this.newProspect.email,
                        mobile: this.newProspect.mobile,
                        source: this.newProspect.source,
                        note: this.newProspect.note
                    };

                    console.log("[" + name + "][saveNewProspect] Before creating prospect back-end call. newProspectDto = ", newProspectDto);

                    const response = await ProspectService.createProspect(this.profile.companyId, newProspectDto);
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    const [prospectData] = await Promise.all([
                        ProspectService.getCompanyProspects(this.profile.companyId)
                    ]);

                    this.prospects = prospectData.data;
                    this.originalProspects = prospectData.data;
                    this.userStore.setProspects(prospectData.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created prospect',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create prospect. Please try again.',
                        life: 3000
                    });
                }
            }

            this.prospect = {};
            this.activeTab = 1;
            this.submitted = false;

        },
        async completeProspect() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.prospect.id) {

                    try {
                        const response = await ProspectService.completeProspect(this.prospect.id);
                        console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                        const [prospectData] = await Promise.all([
                            ProspectService.getCompanyProspects(this.profile.companyId)
                        ]);

                        this.prospects = prospectData.data;
                        this.originalProspects = prospectData.data;
                        this.userStore.setProspects(prospectData.data);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Completed prospect',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to complete prospect. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async sendEmail() {
            console.log("[PhoneCalls][sendEmail] Start.");

            this.submitted = true;

            if (this.prospect.id) {

                try {

                    console.log('[PhoneCalls][updatePhoneCall] this.prospect = ', this.prospect);

                    var toolbox = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        emailSubject: this.toolbox.emailSubject,
                        emailMessage: this.toolbox.emailMessage
                    };

                    console.log("[PmClients][submitToolboxEmail] Before update call. Data = ", toolbox);

                    const response = await ProspectService.sendEmail(this.prospect.id, toolbox);
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Sent email',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to send email. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;

        },
        async sendSms() {
            console.log("[PhoneCalls][sendSms] Start.");

            this.submitted = true;

            if (this.prospect.id) {

                try {

                    console.log('[PhoneCalls][sendSms] this.prospect = ', this.prospect);

                    var toolbox = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        smsMessage: this.toolbox.smsMessage,
                    };

                    console.log("[PmClients][submitToolboxSms] Before update call. Data = ", toolbox);

                    const response = await ProspectService.sendSms(this.prospect.id, toolbox);
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Sent SMS',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to send SMS. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;

        },
        async addAuditTrail() {
            console.log("[PhoneCalls][addAuditTrail] Start.");

            this.submitted = true;

            if (this.prospect.id) {

                try {

                    console.log('[PhoneCalls][updatePhoneCall] this.prospect = ', this.prospect);

                    var toolbox = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        auditTrailSubProcess: this.toolbox.auditTrailSubProcess,
                        auditTrailDescription: this.toolbox.auditTrailDescription
                    };

                    console.log("[PmClients][submitToolboxUnpaid] Before update call. Data = ", toolbox);

                    const response = await ProspectService.updateAuditTrail(this.prospect.id, toolbox);
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Added audit trail',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to add audit trail. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;

        },


    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.customer-badge, .status-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}


.customer-badge {
    &.status-I {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-M {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-Q {
        background: #bc91d5;
        color: #71038f;
    }

    &.status-N {
        background: #28e1a9;
        color: #034f38;
    }

    &.status-P {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-O {
        background: #ecd1be;
        color: #be5306;
    }

    &.status-W {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-L {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-S {
        background: #d4abec;
        color: #bf45bf;
    }
}

.status-badge {
    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #FFCDD2;
        color: #C63737;
    }
}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
